/**
 * ExitReasonDialog — Captures a reason when removing a team member
 *
 * Small confirmation dialog. The PM types a reason (optional but encouraged),
 * then confirms removal. Supports both "remove" (PM-initiated) and
 * "resign" (self-initiated, future) modes via the `mode` prop.
 *
 * DEV-463: Role Lifecycle — Exit Reason Capture
 */

import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import styles from "./ExitReasonDialog.module.css";

type ExitMode = "remove" | "resign";

interface Props {
  isOpen: boolean;
  mode: ExitMode;
  userName: string;
  roleName: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

const ExitReasonDialog: React.FC<Props> = ({
  isOpen,
  mode,
  userName,
  roleName,
  onConfirm,
  onCancel,
}) => {
  const { t } = useAppTranslation("projectTeam");
  const [reason, setReason] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) {
      setReason("");
      // Focus the textarea after a tick so the portal has rendered
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const titleKey = mode === "remove" ? "exitReason.removeTitle" : "exitReason.resignTitle";
  const promptKey = mode === "remove" ? "exitReason.removePrompt" : "exitReason.resignPrompt";
  const confirmKey = mode === "remove" ? "exitReason.confirmRemove" : "exitReason.confirmResign";

  return ReactDOM.createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className={styles.dialog}>
        <h3 className={styles.title}>{t(titleKey)}</h3>
        <p className={styles.prompt}>
          {t(promptKey, { user: userName, role: roleName })}
        </p>
        <textarea
          ref={textareaRef}
          className={styles.reasonInput}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("exitReason.placeholder")}
          rows={3}
        />
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onCancel}
          >
            {t("exitReason.cancel")}
          </button>
          <button
            type="button"
            className={styles.confirmBtn}
            onClick={() => onConfirm(reason.trim())}
          >
            {confirmKey && t(confirmKey)}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ExitReasonDialog;
